import React, { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import LockIcon from '@mui/icons-material/Lock';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';
import { levelProgress } from '../../utils/theFreaks';
import useFreaksT from './useFreaksT';
import FreaksCharacterImage from './FreaksCharacterImage';
import { barSx, FREAKS_DARK, FREAKS_GOLD } from './freaksStyles';

// My freak for this context: who I am now, how far to the next stage and the whole rookie → legend path.
// Stages after the next one stay a mystery ("???"). The path starts scrolled to the current stage.
export default function FreaksCharacter({ character, level }) {
  const { t } = useFreaksT();
  const path = useRef(null);
  useEffect(() => {
    const current = path.current?.children[character.stage - 1];
    if (current) path.current.scrollLeft = Math.max(0, current.offsetLeft - path.current.offsetLeft - current.offsetWidth);
  }, [character.stage]);
  const progress = character.nextLevel ? levelProgress(level, character.fromLevel, character.nextLevel) : 1;
  const stageLabel = (stage) => t('theFreaks.character.stage', { stage, stages: character.stages });

  return (
    <Box data-testid="freaks-character" sx={{ backgroundColor: FREAKS_DARK, color: 'white', borderRadius: 4.5, p: 2, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
        <FreaksCharacterImage imageUrl={character.imageUrl} emoji={character.emoji} size={96}/>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: FREAKS_GOLD, fontSize: 12, fontWeight: 700, letterSpacing: 1 }}>
            {t('theFreaks.character.title').toUpperCase()} · {stageLabel(character.stage)}
          </Typography>
          <Typography sx={{ fontSize: 20, fontWeight: 900, mt: 0.25 }} data-testid="freaks-character-name">{character.name}</Typography>
          <Typography sx={{ color: '#bbb', fontStyle: 'italic', mt: 0.5 }}>{character.tagline}</Typography>
        </Box>
      </Box>

      <LinearProgress variant="determinate" value={progress * 100} sx={{ ...barSx(FREAKS_GOLD), height: 6, borderRadius: 3, mt: 1.75 }}/>
      <Typography sx={{ color: '#999', fontSize: 12, mt: 0.75 }} data-testid="freaks-character-next">
        {character.nextName
          ? t('theFreaks.character.next', { name: character.nextName, level: character.nextLevel })
          : t('theFreaks.character.legend')}
      </Typography>

      <Box ref={path} sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', pt: 1.5 }}>
        {character.path.map((item) => {
          const known = item.unlocked || item.stage === character.stage + 1;
          const current = item.stage === character.stage;
          return (
            <Box
              key={item.stage}
              title={`${stageLabel(item.stage)}: ${known ? item.name : t('theFreaks.character.locked', { level: item.level })}`}
              sx={{
                width: 72, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 0.75, borderRadius: 3,
                backgroundColor: current ? `${DEFAULT_THEME_COLOR}33` : 'transparent',
              }}
            >
              <FreaksCharacterImage imageUrl={item.imageUrl} emoji={character.emoji} size={48} locked={!item.unlocked}/>
              <Typography sx={{ color: '#ddd', fontSize: 11, textAlign: 'center', mt: 0.5, lineHeight: 1.2 }}>{known ? item.name : '???'}</Typography>
              {item.unlocked ? null : (
                <Box sx={{ display: 'flex', alignItems: 'center', color: '#999', mt: 0.25 }}>
                  <LockIcon sx={{ fontSize: 10 }}/>
                  <Typography sx={{ fontSize: 10, ml: 0.25 }}>{t('theFreaks.level', { level: item.level })}</Typography>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
